// Library Imports
import React, { FC, useState } from 'react';

// Local Imports
import { Button, ErrorModal, Spinner, TextField } from '../../components/shared';
import { AvatarIcon } from '../../assets/icons';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { addNewProfileAPI, addProfile, profileAPI, updateProfileAPI } from '../../redux/features/loginUser/loginUserSlice';
import assets from '../../assets';

const Profile: FC = () => {
    const dispatch = useAppDispatch();
    const { profile, loginUser, loading, error, profileLoading } = useAppSelector((state) => state?.currentUser);
    const isProfile = Object.keys(profile)?.length > 0 ? true : false;

    // States
    const [profileImage, setProfileImage] = useState<any>(profile?.data?.profileImage?.Location ? profile?.data?.profileImage?.Location : null);
    const [isError, setIsError] = useState<boolean>(false);
    const [profileInformation, setProfileInformation] = useState<any>({
        firstName: profile?.data?.firstName ? profile?.data?.firstName : "",
        lastName: profile?.data?.lastName ? profile?.data?.lastName : "",
        email: loginUser?.data?.email ? loginUser?.data?.email : "",
        phoneNumber: profile?.data?.phoneNumber ? profile?.data?.phoneNumber : "",
        address: profile?.data?.address ? profile?.data?.address : "",
        state: profile?.data?.state ? profile?.data?.state : "",
        zipCode: profile?.data?.zipcode ? profile?.data?.zipcode : "",
        profileImage: "",
    });
    const [emptyData, setEmptyData] = useState<any>([]);

    // Empty Check Fn
    const emptyCheck = () => {
        let isEmpty: any = {};
        Object.values(profileInformation).map((item, index) => {
            if (item === null || item === '') {
                isEmpty = {
                    ...isEmpty,
                    [Object.keys(profileInformation)[index]]: true,
                };
            }
            return item;
        });
        if (profileInformation.email) {
            delete isEmpty.email;
        }
        if (profileInformation.profileImage === null || profileInformation.profileImage === '') {
            delete isEmpty.profileImage
        }
        setEmptyData(isEmpty);

        return isEmpty ? (Object.keys(isEmpty).length === 0 ? false : true) : false;
    };

    // HandleInputChange
    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'zipCode') {
            setProfileInformation({ ...profileInformation, zipCode: value.replace(/\D/g, '').slice(0, 5) });
        } else {
            setProfileInformation({ ...profileInformation, [name]: value });
        }
        setEmptyData({
            ...emptyData,
            [name]: false,
        });
    };

    // HandleImageChange
    const handleImageChange = (e: any) => {
        const file = e.target.files[0];
        setProfileInformation({ ...profileInformation, profileImage: file });
    };

    // HandleSaveButton
    const saveButtonClicked = async () => {
        if (!emptyCheck()) {
            let formData = new FormData();
            formData.append('firstName', profileInformation.firstName?.trim());
            formData.append('lastName', profileInformation.lastName?.trim());
            formData.append('email', profileInformation.email);
            formData.append('phoneNumber', profileInformation.phoneNumber?.trim());
            formData.append('address', profileInformation.address?.trim());
            formData.append('state', profileInformation.state?.trim());
            formData.append('zipcode', profileInformation.zipCode?.trim());
            formData.append('profileImage', profileInformation?.profileImage);
            const response = await dispatch(updateProfileAPI({ payload: formData, id: loginUser?.data?.profile?._id || profile?.data?._id }));
            if (response.payload.status === "success") {
                const res = await dispatch(profileAPI())
                if (res.payload.status === "success") {
                    const data = res.payload.data;
                    setProfileImage(data?.profileImage.Location ? data?.profileImage.Location : null);
                    setProfileInformation({
                        ...profileInformation,
                        firstName: data?.firstName,
                        lastName: data?.lastName,
                        phoneNumber: data?.phoneNumber,
                        address: data?.address,
                        state: data?.state,
                        zipCode: data?.zipcode,
                    })
                    return
                }
            }
            setIsError(true);
        }
    };

    // HandleAddNewProfileClicked
    const addNewProfileClicked = async () => {
        if (!emptyCheck()) {
            let formData = new FormData();
            formData.append('firstName', profileInformation.firstName?.trim());
            formData.append('lastName', profileInformation.lastName?.trim());
            formData.append('email', profileInformation.email);
            formData.append('phoneNumber', profileInformation.phoneNumber?.trim());
            formData.append('address', profileInformation.address?.trim());
            formData.append('state', profileInformation.state?.trim());
            formData.append('zipcode', profileInformation.zipCode?.trim());
            formData.append('profileImage', profileInformation?.profileImage);
            const response = await dispatch(addNewProfileAPI({ payload: formData }));
            if (response.payload.status === "success") {
                dispatch(addProfile(response.payload.data));
                const res = await dispatch(profileAPI())
                if (res.payload.status === "success") {
                    const data = res.payload.data;
                    setProfileImage(data?.profileImage.Location ? data?.profileImage.Location : null);
                    setProfileInformation({
                        ...profileInformation,
                        firstName: data?.firstName,
                        lastName: data?.lastName,
                        phoneNumber: data?.phoneNumber,
                        address: data?.address,
                        state: data?.state,
                        zipCode: data?.zipcode,
                        profileId: data?._id
                    })
                    return
                }
            }
            setIsError(true);
        }
    }

    return (
        <>
            {profileLoading ?
                <Spinner />
                :
                <div className='flex flex-col gap-2 m-3 rounded-lg border border-gray-200 '>
                    {/* Error Modal */}
                    <ErrorModal
                        showModal={isError}
                        setShowModal={setIsError}
                        errorMsg={error}
                    />

                    <div className='flex flex-col gap-2 p-3'>

                        {/* Heading & Profile Image */}
                        <React.Fragment>
                            <h1 className='text-xl ml-1'>Personal Information</h1>
                            <div className="w-full mb-2">
                                <div className="relative drop-shadow-2 flex gap-2 items-center">
                                    {profileInformation.profileImage === '' && profileImage === null ? <AvatarIcon className='w-20 h-20 text-gray-400' />
                                        :
                                        <img
                                            src={profileImage ? profileImage : URL.createObjectURL(profileInformation.profileImage)}
                                            onError={(e) => { e.currentTarget.src = assets.avatar; e.currentTarget.className = 'w-20 h-20 border-none object-cover object-center rounded-full' }}
                                            className='w-20 h-20 border object-cover object-center rounded-full'
                                            alt="Profile"
                                        />

                                    }
                                    <label
                                        htmlFor="profile">
                                        <div className='cursor-pointer w-full px-4 py-[10px] text-sm font-medium tracking-wide capitalize transition-colors duration-300 transform rounded-lg focus:outline-none focus:ring-1 focus:ring-opacity-50 text-white bg-primary hover:bg-primary/70 focus:ring-primary/60'> Upload Image</div>
                                        <input
                                            type="file"
                                            name="profile"
                                            id="profile"
                                            className="sr-only"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            onClick={() => {
                                                setProfileImage(null);
                                            }}
                                        />
                                    </label>
                                    {
                                        (profileInformation.profileImage !== '' || profileImage !== null) && <span
                                            className="cursor-pointer px-4 py-[10px] text-sm font-medium tracking-wide capitalize transition-colors duration-300 transform border  rounded-lg hover:outline-none hover:ring-1 hover:ring-opacity-50 focus:outline-none text-primary border-primary/80 hover:border-primary/70 hover:ring-primary/60"
                                            onClick={() => { setProfileInformation({ ...profileInformation, profileImage: '' }); setProfileImage(null) }} >
                                            Remove
                                        </span>
                                    }
                                </div>
                            </div>
                        </React.Fragment>

                        {/* Form & Save Button */}
                        <div className='w-full'>

                            <div className="flex flex-col lg:flex-row gap-x-6">

                                <div className="w-full lg:w-1/2">
                                    <div className="mb-3">
                                        <TextField
                                            placeholder="First Name"
                                            name="firstName"
                                            value={profileInformation.firstName}
                                            error={emptyData.firstName}
                                            errorMsg={emptyData.firstName && "First Name is required"}
                                            onChange={handleOnChange}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <TextField
                                            placeholder="Last Name"
                                            name="lastName"
                                            value={profileInformation.lastName}
                                            error={emptyData.lastName}
                                            errorMsg={emptyData.lastName && "Last Name is required"}
                                            onChange={handleOnChange}
                                        />
                                    </div>

                                    <div className="mb-3 relative">
                                        <TextField
                                            placeholder="Email"
                                            type="email"
                                            name='email'
                                            disabled
                                            value={profileInformation.email || ""}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <TextField
                                            placeholder="Zip Code"
                                            name="zipCode"
                                            value={profileInformation.zipCode || ""}
                                            onChange={handleOnChange}
                                            errorMsg={emptyData.zipCode && "Zip Code is required"}
                                            error={emptyData.zipCode}
                                        />
                                    </div>
                                </div>

                                <div className="w-full lg:w-1/2">

                                    <div className="mb-3">
                                        <TextField
                                            placeholder="Phone"
                                            name="phoneNumber"
                                            value={profileInformation.phoneNumber || ""}
                                            errorMsg={emptyData.phoneNumber && "Phone is required"}
                                            error={emptyData.phoneNumber}
                                            onChange={handleOnChange}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <TextField
                                            placeholder="State"
                                            name="state"
                                            errorMsg={emptyData.state && "State is required"}
                                            error={emptyData.state}
                                            value={profileInformation.state || ""}
                                            onChange={handleOnChange}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <TextField
                                            placeholder="Address"
                                            name="address"
                                            value={profileInformation.address || ""}
                                            onChange={handleOnChange}
                                            error={emptyData.address}
                                            errorMsg={emptyData.address && "Address is required"}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    loading={loading}
                                    onClick={isProfile ? saveButtonClicked : addNewProfileClicked}>
                                    {isProfile ? "Save" : 'Add'}
                                </Button>
                            </div>


                        </div>
                    </div>
                </div>
            }
        </>
    );
};

export default Profile;
